import type { AbortSignal as SdkAbortSignal } from "@aws-sdk/types";
import { ListWcClaimsInput, ListWcClaimsCommand } from "@amzn/ttechclaimintakeservice-client";

import { getAuthenticatedClient } from "../setup/getAuthenticatedClient";
import { MetricsPublisher } from "../../metrics/metrics";

/**
 * Extended input type for listWcClaims that includes the countries parameter.
 * The backend may not yet support this field, so it is optional for forward compatibility.
 */
export interface ExtendedListWcClaimsInput extends ListWcClaimsInput {
  countries?: string[];
}

/**
 * Lists Workers' Compensation claims.
 *
 * An optional `AbortSignal` cancels the in-flight request so a superseded or
 * timed-out search stops consuming a Lambda invocation instead of running to
 * completion. When aborted, the `AbortError` is re-thrown so callers can tell
 * a cancellation apart from a real service failure.
 */
export async function listWcClaimsService(input: ExtendedListWcClaimsInput, signal?: AbortSignal) {
  const { client } = await getAuthenticatedClient();
  if (!client) {
    return null;
  }
  const command = new ListWcClaimsCommand(input);
  const metricsPublisher = new MetricsPublisher("api.listWcClaims");

  return await client
    .send(command, { abortSignal: signal as unknown as SdkAbortSignal })
    .then((response) => {
      metricsPublisher.publishAuthenticationLatencyMetrics();
      metricsPublisher.publishRenderSuccessMetrics();
      return response;
    })
    .catch((error: unknown) => {
      // A cancellation is caller-initiated, so publishing a failure metric here
      // would report an operational problem that did not happen.
      if (error instanceof Error && error.name === "AbortError") {
        throw error;
      }
      metricsPublisher.publishAuthenticationLatencyMetrics();
      metricsPublisher.publishRenderFatalMetrics();
      throw error;
    });
}
